import { useSelector } from 'react-redux';
import { statisticSelector } from '@/redux';
import { useMemo } from 'react';
import { IAuthUser } from '@/types/user';
import { IUser } from '@/interfaces';
import { eachDayOfInterval, isWeekend, startOfMonth, endOfMonth } from 'date-fns';

const toHoursAndMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return { h, m };
};

const useHoursCard = (user: IAuthUser | IUser) => {
  const { limit, minutes } = useSelector(statisticSelector);
  const expected = useMemo(() => Math.round(limit / 60), [limit]);

  const overtime = useMemo(() => {
    const userOvertimePercent = user?.overTimePercent ? user.overTimePercent / 100 : 0;

    const h = Math.floor((limit * userOvertimePercent) / 60);
    return `${h} h`;
  }, [limit, user?.overTimePercent]);

  const progress = useMemo(() => Math.round((minutes / 60 / expected) * 100), [expected, minutes]);

  const time = useMemo(() => toHoursAndMinutes(minutes), [minutes]);

  const someday = new Date();
  const defaultWorkingHours =
    eachDayOfInterval({
      start: startOfMonth(someday),
      end: endOfMonth(someday),
    }).filter((day) => !isWeekend(day)).length * 8;

  const helpMessage = `There are ${defaultWorkingHours} hours + ${Math.floor(
    defaultWorkingHours * ((user.overTimePercent ?? 0) / 100),
  )} hours of overtime`;

  return { time, expected, overtime, progress, percent: user?.overTimePercent, helpMessage };
};

export default useHoursCard;
